import * as RadixCheckbox from '@radix-ui/react-checkbox';
import { type VariantProps, tv } from 'tailwind-variants';

import Text from '../base/Text';
import CheckedIcon from '../icons/CheckedIcon';
import RadioFilledIcon from '../icons/RadioFilledIcon';
import RadioUnfilledIcon from '../icons/RadioUnfilledIcon';
import UncheckedIcon from '../icons/UncheckedIcon';

const checkbox = tv({
  base: 'cursor-pointer',
  variants: {
    color: {
      default: 'text-gray-90 dark:text-gray-30',
      settings: 'text-blue data-[state=unchecked]:text-gray-60',
    },
    disabled: {
      true: 'text-gray-90 bg-gray-30 cursor-not-allowed opacity-50',
      false: 'cursor-pointer',
    },
  },
  defaultVariants: {
    color: 'default',
  },
});

export default function Checkbox({
  id,
  checked,
  className,
  label,
  labelSecondary,
  setChecked,
  variant = 'radio',
  color,
  disabled,
}: {
  id: string;
  checked: boolean;
  className?: string;
  label?: string;
  labelSecondary?: string;
  setChecked: (checked: boolean) => void;
  variant?: 'radio' | 'checkbox';
  color?: VariantProps<typeof checkbox>['color'];
  disabled?: boolean;
}) {
  return (
    <div className={`flex items-end gap-1.5 ${className}`}>
      <RadixCheckbox.Root
        className={checkbox({ color, disabled })}
        id={id}
        checked={checked}
        onCheckedChange={setChecked}
        disabled={disabled}
      >
        {checked === true ? (
          <RadixCheckbox.Indicator>
            {variant === 'checkbox' ? <CheckedIcon /> : <RadioFilledIcon />}
          </RadixCheckbox.Indicator>
        ) : (
          <>
            {variant === 'checkbox' ? <UncheckedIcon /> : <RadioUnfilledIcon />}
          </>
        )}
      </RadixCheckbox.Root>
      <Text
        as="label"
        variant="studio/body/medium/sm"
        className={`cursor-pointer ${
          color === 'settings'
            ? 'text-gray-60 dark:text-gray-30'
            : 'text-gray-90 dark:text-white'
        } ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
        htmlFor={id}
      >
        {label}
      </Text>
      {labelSecondary && (
        <Text
          as="label"
          variant="studio/body/medium/sm"
          className={`text-gray-60 dark:text-gray-30 cursor-pointer`}
        >
          {labelSecondary}
        </Text>
      )}
    </div>
  );
}
